import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { PROJECT_NAME_TOOLTIP } from "../../../../constants";
import { FormFieldGroup, FormRow, FormSection } from "../../../form";
import {
	FormCurrencyComponent,
	FormDateComponent,
	FormInputComponent,
	FormYesNoComponent,
} from "../../../form/fieldLayouts";

export type BidToFinalFieldNames<TFieldValues extends FieldValues> = {
	actualBidAmount: Path<TFieldValues>;
	secondBid: Path<TFieldValues>;
	bondAmount: Path<TFieldValues>;
	contractAmount: Path<TFieldValues>;
	actualBidDate?: Path<TFieldValues>;
	contractDate?: Path<TFieldValues>;
	tListingRequired?: Path<TFieldValues>;
	projectName?: Path<TFieldValues>;
};

type BidToFinalFieldsProps<TFieldValues extends FieldValues> = {
	formMethods: UseFormReturn<TFieldValues>;
	names: BidToFinalFieldNames<TFieldValues>;
	minimal: boolean;
	showRequiredMarkers?: boolean;
	onBondAmountBlur: () => void;
	onContractAmountChange: () => void;
};

export const BidToFinalFields = <TFieldValues extends FieldValues>({
	formMethods,
	names,
	minimal,
	showRequiredMarkers,
	onBondAmountBlur,
	onContractAmountChange,
}: BidToFinalFieldsProps<TFieldValues>) => (
	<>
		<FormSection header="Bid Results">
			<FormFieldGroup>
				<FormRow>
					<FormCurrencyComponent
						formMethods={formMethods}
						name={names.actualBidAmount}
						label="Low Bid Amount (Principal's Bid)"
						required={showRequiredMarkers}
						testId="low-bid-amount-input"
					/>
					<FormCurrencyComponent
						formMethods={formMethods}
						name={names.secondBid}
						label="Second Lowest Bid"
						testId="second-bid-amount-input"
					/>
				</FormRow>
				{!minimal && names.actualBidDate && (
					<FormDateComponent
						formMethods={formMethods}
						name={names.actualBidDate}
						label="Bid Date"
					/>
				)}
			</FormFieldGroup>
		</FormSection>

		<FormSection header="Final Bond" topPadding>
			<FormFieldGroup>
				<FormRow>
					<FormCurrencyComponent
						formMethods={formMethods}
						name={names.bondAmount}
						label="Bond Amount"
						required={showRequiredMarkers}
						testId="bond-amount-input"
						onValueBlur={onBondAmountBlur}
					/>
					<FormCurrencyComponent
						formMethods={formMethods}
						name={names.contractAmount}
						label="Contract Amount"
						required={showRequiredMarkers}
						testId="contract-amount-input"
						onValueChange={onContractAmountChange}
					/>
				</FormRow>
				{!minimal && names.contractDate && (
					<FormDateComponent
						formMethods={formMethods}
						name={names.contractDate}
						label="Contract Date"
					/>
				)}
				{!minimal && names.tListingRequired && (
					<FormYesNoComponent
						formMethods={formMethods}
						name={names.tListingRequired}
						label="Does the final bond require a Treasury Listing?"
						deselectable
					/>
				)}
				{minimal && names.projectName && (
					<FormInputComponent
						formMethods={formMethods}
						name={names.projectName}
						label="Project Name"
						tooltip={PROJECT_NAME_TOOLTIP}
						required={showRequiredMarkers}
						testId="project-name-input"
					/>
				)}
			</FormFieldGroup>
		</FormSection>
	</>
);
